import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import Assets from "./components/Assets";
import Attendance from "./components/Attendance";
import Broadcasts from "./components/Broadcasts";
import Dashboard from "./components/Dashboard";
import AdminDashboard from "./components/AdminDashboard";
import HRDashboard from "./components/HRDashboard";
import MarketingDashboard from "./components/MarketingDashboard";
import Employees from "./components/Employees";
import Goals from "./components/Goals";
import MyCalendar from "./components/MyCalendar";
import Leaves from "./components/Leaves";
import Login from "./components/Login";
import Navbar from "./components/Navbar";
import Payroll from "./components/Payroll";
import MyPayslip from "./components/MyPayslip";
import Profile from "./components/Profile";
import UserProfile from "./components/UserProfile";
import Projects from "./components/Projects";
import Assignments from "./components/Assignments";
import Clients from "./components/Clients";
import Notifications from "./components/Notifications";
import Toast from "./components/Toast";
import Deals from "./components/Deals";
import ClientPayments from "./components/ClientPayments";
import Roles from "./components/Roles";
import SalesTeams from "./components/SalesTeams";
import SalesReportsDashboard from "./components/SalesReportsDashboard";
import RecruitmentDashboard from "./components/RecruitmentDashboard";
import JobsList from "./components/JobsList"; // Renamed AddJob.js to this conceptually in previous step, now linking
import PostJob from "./components/AddJob"; // Keeping AddJob as the form component but linked differently
import AddCandidate from "./components/AddCandidate";
import ScheduleInterview from "./components/ScheduleInterview";
import RecruitmentPipeline from "./components/RecruitmentPipeline";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Devices from "./components/biometric/Devices";
import BiometricAttendance from "./components/biometric/Attendance";
import Documents from "./components/Documents";
import MyDocuments from "./components/MyDocuments";
import DocumentGenerator from "./components/DocumentGenerator";
import Proposals from "./components/Proposals";
import Templates from "./components/Templates";
import Letters from "./components/Letters";

import Settings from "./components/Settings";
import MyLeads from "./components/MyLeads";
import MarketingLeads from "./components/MarketingLeads";
import Activities from "./components/Activities";

// Wrapper component to show correct leads page based on role
function LeadsWrapper() {
  const { user, hasPermission } = useAuth();
  const isMarketing = user?.role?.name?.toLowerCase().includes('marketing');
  const canRead = hasPermission('leads', 'read');

  // Marketing role sees Marketing Leads page
  if (isMarketing) {
    return <MarketingLeads />;
  }

  // All other roles with READ permission see My Leads page
  if (canRead) {
    return <MyLeads />;
  }

  // No permission - redirect to home
  return <Navigate to="/" replace />;
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#f4f7f0", padding: "20px 24px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <div style={{ height: 58, background: "#ffffff", borderRadius: 12, border: "1px solid #eef2f3", marginBottom: 14 }} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, marginBottom: 14 }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} style={{ height: 110, background: "#ffffff", borderRadius: 12, border: "1px solid #eef2f3" }} />
            ))}
          </div>
          <div style={{ height: 340, background: "#ffffff", borderRadius: 12, border: "1px solid #eef2f3", marginBottom: 14 }} />
          <div style={{ height: 240, background: "#ffffff", borderRadius: 12, border: "1px solid #eef2f3" }} />
        </div>
      </div>
    );
  }
  return user ? children : <Navigate to="/login" />;
}

function SalesRoute({ children }) {
  const { user } = useAuth();
  if (!user || user.role?.name !== 'Sales') return <Navigate to="/" />;
  return children;
}

// Redirects to "/" if the user doesn't have read permission for the given module.
// Use adminOnly=true for pages that are admin-exclusive.
function PermissionRoute({ module, adminOnly, children }) {
  const { user, hasPermission } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (adminOnly) {
    // For admin-only routes, check if user has admin role
    if (user?.role?.name !== 'Admin') return <Navigate to="/" />;
    return children;
  }
  // Check permission using the hasPermission function
  const hasRead = hasPermission(module, 'read');
  if (!hasRead) return <Navigate to="/" />;
  return children;
}

function AppContent() {
  const { user, activeToast, setActiveToast } = useAuth();

  return (
    <>
      {user && <Navbar />}
      <Toast notification={activeToast} onClose={() => setActiveToast(null)} />
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              {user?.role?.name === 'Admin'
                ? <AdminDashboard />
                : user?.role?.name?.toLowerCase().includes('marketing')
                  ? <MarketingDashboard />
                  : user?.role?.name === 'HR'
                    ? <HRDashboard />
                    : <Dashboard />}
            </PrivateRoute>
          }
        />
        <Route
          path="/broadcasts"
          element={
            <PermissionRoute module="broadcasts">
              <Broadcasts />
            </PermissionRoute>
          }
        />
        <Route
          path="/recruitment/dashboard"
          element={
            <PermissionRoute module="recruitment">
              <RecruitmentDashboard />
            </PermissionRoute>
          }
        />
        <Route
          path="/recruitment/add-job"
          element={
            <PermissionRoute module="recruitment">
              <JobsList />
            </PermissionRoute>
          }
        />
        <Route
          path="/recruitment/jobs"
          element={
            <PermissionRoute module="recruitment">
              <JobsList />
            </PermissionRoute>
          }
        />
        <Route
          path="/recruitment/create-job"
          element={
            <PermissionRoute module="recruitment">
              <PostJob />
            </PermissionRoute>
          }
        />
        <Route
          path="/recruitment/add-candidate"
          element={
            <PermissionRoute module="recruitment">
              <AddCandidate />
            </PermissionRoute>
          }
        />
        <Route
          path="/recruitment/candidate-database"
          element={
            <PermissionRoute module="recruitment">
              <AddCandidate />
            </PermissionRoute>
          }
        />
        <Route
          path="/recruitment/schedule-interview"
          element={
            <PermissionRoute module="recruitment">
              <ScheduleInterview />
            </PermissionRoute>
          }
        />
        <Route
          path="/recruitment/interview-schedule"
          element={
            <PermissionRoute module="recruitment">
              <ScheduleInterview />
            </PermissionRoute>
          }
        />
        <Route
          path="/recruitment/pipeline"
          element={
            <PermissionRoute module="recruitment">
              <RecruitmentPipeline />
            </PermissionRoute>
          }
        />
        <Route
          path="/recruitment/job-applications"
          element={
            <PermissionRoute module="recruitment">
              <RecruitmentPipeline />
            </PermissionRoute>
          }
        />

        <Route
          path="/calendar"
          element={
            <PermissionRoute module="calendar">
              <MyCalendar />
            </PermissionRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <PermissionRoute module="projects">
              <Projects />
            </PermissionRoute>
          }
        />
        <Route
          path="/assignments"
          element={
            <PermissionRoute module="assignments">
              <Assignments />
            </PermissionRoute>
          }
        />
        <Route
          path="/clients"
          element={
            <PermissionRoute module="clients">
              <Clients />
            </PermissionRoute>
          }
        />
        <Route
          path="/deals"
          element={
            <PermissionRoute module="deals">
              <Deals />
            </PermissionRoute>
          }
        />
        <Route
          path="/client-payments"
          element={
            <PermissionRoute module="accounts">
              <ClientPayments />
            </PermissionRoute>
          }
        />
        <Route
          path="/goals"
          element={
            <PermissionRoute module="goals">
              <Goals />
            </PermissionRoute>
          }
        />
        <Route
          path="/assets"
          element={
            <PermissionRoute module="assets">
              <Assets />
            </PermissionRoute>
          }
        />
        <Route
          path="/leaves"
          element={
            <PermissionRoute module="leaves">
              <Leaves />
            </PermissionRoute>
          }
        />
        <Route
          path="/attendance"
          element={
            <PermissionRoute module="attendance">
              <Attendance />
            </PermissionRoute>
          }
        />
        <Route
          path="/leads"
          element={
            <PrivateRoute>
              <LeadsWrapper />
            </PrivateRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              {user?.role?.name === 'Admin' ? <Profile /> : <UserProfile />}
            </PrivateRoute>
          }
        />
        <Route
          path="/roles"
          element={
            <PermissionRoute module="roles">
              <Roles />
            </PermissionRoute>
          }
        />
        <Route
          path="/employees"
          element={
            <PermissionRoute module="employees">
              <Employees />
            </PermissionRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <PermissionRoute module="documents">
              <Documents />
            </PermissionRoute>
          }
        />
        <Route
          path="/my-documents"
          element={
            <PermissionRoute module="my-documents">
              <MyDocuments />
            </PermissionRoute>
          }
        />
        <Route
          path="/document-generator"
          element={
            <PermissionRoute module="documents">
              <DocumentGenerator />
            </PermissionRoute>
          }
        />
        <Route
          path="/payroll"
          element={
            <PermissionRoute module="payroll">
              <Payroll />
            </PermissionRoute>
          }
        />
        <Route
          path="/devices"
          element={
            <PermissionRoute module="devices">
              <Devices />
            </PermissionRoute>
          }
        />
        <Route
          path="/biometric-attendance"
          element={
            <PermissionRoute module="biometric-logs">
              <BiometricAttendance />
            </PermissionRoute>
          }
        />
        <Route
          path="/my-payroll"
          element={
            <PermissionRoute module="my-payslips">
              <MyPayslip />
            </PermissionRoute>
          }
        />
        <Route
          path="/finance"
          element={
            <PermissionRoute module="my-payslips">
              <MyPayslip />
            </PermissionRoute>
          }
        />
        <Route
          path="/sales-teams"
          element={
            <PermissionRoute module="sales">
              <SalesTeams />
            </PermissionRoute>
          }
        />
        <Route
          path="/sales-reports"
          element={
            <PermissionRoute module="sales-reports">
              <SalesReportsDashboard />
            </PermissionRoute>
          }
        />
        <Route
          path="/proposals"
          element={
            <PermissionRoute module="proposals">
              <Proposals />
            </PermissionRoute>
          }
        />

        <Route
          path="/templates"
          element={
            <PermissionRoute module="proposals">
              <Templates />
            </PermissionRoute>
          }
        />
        <Route
          path="/letters"
          element={
            <PermissionRoute module="letters">
              <Letters />
            </PermissionRoute>
          }
        />
        <Route
          path="/activities"
          element={
            <PermissionRoute module="activities">
              <Activities />
            </PermissionRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <PrivateRoute>
              <Notifications />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <PermissionRoute adminOnly>
              <Settings />
            </PermissionRoute>
          }
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
